// navCatalog — the canonical map of "what an operator might say" → real tab key.
//
// This is the single source of truth for the ARIA natural-language
// navigator ("I wanna upload leads" → Leads). The client sends this
// catalog to /api/ai/aria-command with every command, so the server
// NEVER carries a hardcoded (and drift-prone) tab list — the router can
// only ever land on a key that actually exists in this file.
//
// Each entry:
//   tab   — the real hash-route key (#tab=<tab>) the app navigates to
//   label — human name shown in ARIA's confirmation ("Opening Leads")
//   kw    — synonyms / phrasings an operator might type. Matched as
//           whole-word-ish substrings, case-insensitive. Put the most
//           distinctive phrasings first; generic single words last.
//
// Keys here are verified against PALETTE_TABS + the sidebar nav registry
// in CommissionTracker.jsx. When you add a destination to the app, add
// it here too — that is the only place the navigator learns about it.

export const NAV_CATALOG = [
  // ── Prospecting / pipeline ──────────────────────────────────────
  { tab: 'leads', label: 'Leads',
    kw: ['upload leads', 'upload lead', 'import leads', 'import lead', 'add leads', 'add a lead',
         'new lead', 'add lead', 'import csv', 'upload csv', 'bulk import', 'lead list', 'my leads',
         'prospects', 'leads', 'import contacts', 'add contact', 'add prospect'] },
  { tab: 'next_call', label: 'Call Next',
    kw: ['call next', 'who to call', 'who should i call', 'next best call', 'next call', 'call now',
         'who do i call', 'best call', 'call queue', 'work my leads', 'start calling', 'next lead'] },
  { tab: 'no_show_recovery', label: 'No-Show Recovery',
    kw: ['no show', 'no-show', 'noshow', 'missed appointment', 'missed appointments', 'recover no show',
         'recovery queue', 'didn\'t show', 'missed appt', 'no show recovery'] },
  { tab: 'booking_scorecard', label: 'Booking Scorecard',
    kw: ['booking scorecard', 'show rate', 'show-rate', 'no show rate', 'setter scorecard', 'source quality',
         'which source shows up', 'appointment quality', 'booked vs showed', 'setter performance'] },
  { tab: 'command', label: 'Command Center',
    kw: ['command center', 'sales floor', 'sales command', 'command'] },
  { tab: 'kanban', label: 'Pipeline (Kanban)',
    kw: ['pipeline', 'kanban', 'deal board', 'pipeline board', 'stages'] },
  { tab: 'power_dialer', label: 'Power Dialer',
    kw: ['power dialer', 'start dialing', 'start calling', 'dialer', 'dial', 'make calls', 'call queue', 'phone'] },
  { tab: 'calls', label: 'Calls Log',
    kw: ['call log', 'calls log', 'call history', 'recent calls', 'calls'] },
  { tab: 'follow_up', label: 'Follow-Up Engine',
    kw: ['follow up', 'follow-ups', 'followups', 'who to call', 'next lead', 'hot leads', 'today\'s list', 'ranked leads', 'chase'] },
  { tab: 'tasks', label: 'Tasks & Reminders',
    kw: ['tasks', 'reminders', 'to do', 'todo', 'to-do', 'my tasks'] },
  { tab: 'field_sales', label: 'Field Command',
    kw: ['field sales', 'field command', 'community outreach', 'walmart', 'event booth', 'field prospects',
         'take it home', 'take-it-home', 'qr code', 'qr intake', 'check in shift', 'field shift', 'outreach'] },
  { tab: 'landing_pages', label: 'Landing Pages',
    kw: ['landing page', 'landing pages', 'build a page', 'create a page', 'funnel', 'canvas', 'page builder',
         'lead page', 'quote page', 'marketing page', 'website builder', 'ai page', 'publish a page'] },
  { tab: 'prospect_queue', label: 'My New Prospects',
    kw: ['new prospects', 'my prospects', 'prospect queue', 'work queue', 'leads to call', 'new leads to contact',
         'sla', 'who to contact', 'contact now', 'overdue leads', 'uncontacted'] },
  { tab: 'lead_dashboard', label: 'Lead Command',
    kw: ['lead dashboard', 'lead command', 'manager dashboard', 'lead analytics', 'conversion by source',
         'lead volume', 'sla health', 'leads today', 'funnel', 'by product line', 'manager view'] },
  { tab: 'aca_oep', label: 'ACA OEP Mission Control',
    kw: ['aca oep', 'open enrollment', 'oep mission control', 'aca mission control', 'marketplace season',
         'oep dashboard', 'aca renewals', 'aca season', 'aca priorities', 'oep control'] },
  { tab: 'agent_protection', label: 'Agent Protection',
    kw: ['agent protection', 'evidence', 'evidence vault', 'allegation', 'complaint defense', 'consent evidence',
         'audit trail', 'chain of custody', 'tamper', 'proof', 'e&o', 'investigation', 'legal hold'] },
  { tab: 'carrier_portals', label: 'Carrier Portals',
    kw: ['carrier portal', 'carrier portals', 'member portal', 'portal setup', 'send portal', 'registration link',
         'member account', 'carrier directory', 'id card', 'carrier app', 'payment portal'] },
  { tab: 'nova', label: 'Nova Command Center',
    kw: ['nova', 'ask nova', 'command center', 'what needs attention', 'who to call next', 'ai operations',
         'executive overview', 'operations director', 'whats happening', 'end of day review'] },
  { tab: 'team', label: 'Team',
    kw: ['team', 'roster', 'add agent', 'invite agent', 'roles', 'permissions', 'staff', 'members',
         'manager', 'assign leads', 'reassign', 'setter', 'closer', 'agency team'] },
  { tab: 'system_health', label: 'System Health',
    kw: ['system health', 'health check', 'qa audit', 'run system check', 'status', 'diagnostics',
         'uptime', 'broken', 'is anything down', 'admin health'] },
  { tab: 'setup_checklist', label: 'Setup',
    kw: ['setup', 'onboarding', 'checklist', 'am i ready', 'go live', 'test my number', 'test phone',
         'test text', 'test call', 'get started', 'account ready'] },
  { tab: 'import_leads', label: 'Import Leads',
    kw: ['import leads', 'import', 'upload leads', 'paste leads', 'bulk import', 'lead vendor', 'csv',
         'spreadsheet', 'book of business', 'organize with ai', 'sage', 'lead file'] },
  { tab: 'medicare_readiness', label: 'Medicare Safe-to-Proceed',
    kw: ['medicare readiness', 'safe to proceed', 'soa', 'scope of appointment', 'medicare compliance',
         'can i sell', 'compliance gate', 'am i cleared', 'ptc', 'permission to contact'] },
  { tab: 'people', label: 'People',
    kw: ['people', 'person', 'household', 'identity', 'unify', 'merge', 'contact', 'contradiction',
         'same person', 'cross sell', 'household map', 'one identity'] },
  { tab: 'growth', label: 'Growth Studio',
    kw: ['growth', 'growth studio', 'attribution', 'funnel by source', 'what is producing', 'roi',
         'campaign performance', 'cost per lead', 'cost per enrollment', 'channels', 'source map',
         'which campaign', 'marketing performance', 'scans to enrollment', 'conversion by source'] },

  // ── Messaging / comms ───────────────────────────────────────────
  { tab: 'inbox', label: 'Inbox',
    kw: ['inbox', 'messages', 'texts', 'sms inbox', 'text messages', 'replies', 'unread', 'conversations', 'my texts'] },
  { tab: 'email', label: 'Email',
    kw: ['email', 'compose email', 'send email', 'write email'] },
  { tab: 'workflows', label: 'Workflows & Drips',
    kw: ['workflows', 'drips', 'automations', 'sequences', 'drip campaign', 'automation'] },

  // ── Calendar ────────────────────────────────────────────────────
  { tab: 'calendar', label: 'Calendar',
    kw: ['calendar', 'schedule', 'my schedule', 'agenda', 'appointments', 'see my calendar'] },
  { tab: 'health_calendar', label: 'Calendar (add appointment)',
    kw: ['add appointment', 'book appointment', 'new appointment', 'schedule appointment', 'book a meeting', 'set appointment'] },

  // ── Clients / book ──────────────────────────────────────────────
  { tab: 'clients', label: 'Clients',
    kw: ['clients', 'customers', 'client list', 'my clients', 'policyholders', 'book of clients', 'find client'] },
  { tab: 'book', label: 'Book of Business',
    kw: ['book of business', 'my book', 'book snapshot', 'client movement', 'fell off', 'lost clients', 'aor'] },
  { tab: 'retention', label: 'Retention',
    kw: ['retention', 'at risk', 'at-risk', 'save clients', 'losing clients', 'cancellations', 'who is cancelling', 'who will cancel'] },
  { tab: 'retention_command', label: 'Retention & Chargeback Command',
    kw: ['chargeback', 'chargebacks', 'failed payment', 'nsf', 'clawback', 'persistency'] },

  // ── Policies / money ────────────────────────────────────────────
  { tab: 'life_leads', label: 'Life Leads',
    kw: ['life leads', 'final expense leads', 'iul leads', 'term leads', 'mortgage protection leads',
         'life prospects', 'life pipeline', 'fe leads', 'annuity leads', 'life insurance leads'] },
  { tab: 'policies', label: 'Policy Vault (Life)',
    kw: ['policies', 'policy vault', 'life policies', 'policy list'] },
  { tab: 'paycalc', label: 'My Pay',
    kw: ['my pay', 'commission', 'commissions', 'paycheck', 'how much did i make', 'earnings', 'payout'] },
  { tab: 'pnl', label: 'P&L',
    kw: ['p&l', 'pnl', 'profit and loss', 'profit', 'net income'] },
  { tab: 'transactions', label: 'Transactions',
    kw: ['transactions', 'payments received', 'deposits'] },
  { tab: 'expenses', label: 'Expenses',
    kw: ['expenses', 'log expense', 'spending', 'add expense', 'costs'] },

  // ── Health / Medicare / ACA ─────────────────────────────────────
  { tab: 'health_portal', label: 'Health Portal',
    kw: ['health portal', 'health workspace', 'health home'] },
  { tab: 'aca', label: 'ACA / Health Workspace',
    kw: ['aca', 'obamacare', 'marketplace', 'health insurance workspace'] },
  { tab: 'medicare', label: 'Medicare Workspace',
    kw: ['medicare', 'med supp', 'medicare advantage', 'medigap'] },

  // ── Agency / AI / analytics ─────────────────────────────────────
  { tab: 'dashboard', label: 'Agency Dashboard',
    kw: ['dashboard', 'agency dashboard', 'overview'] },
  { tab: 'agency_analytics', label: 'Agency Analytics',
    kw: ['analytics', 'kpis', 'kpi', 'metrics', 'performance', 'scorecard', 'stats'] },
  { tab: 'leaderboard', label: 'Leaderboard',
    kw: ['leaderboard', 'rankings', 'top producers', 'standings'] },
  { tab: 'ai_agents', label: 'AI Agents Hub',
    kw: ['ai agents', 'ai staff', 'ai team', 'ai employees', 'ai workforce', 'my ai', 'agents hub'] },
  { tab: 'ai_boss', label: 'AI Boss',
    kw: ['ai boss', 'ai ceo', 'run my agency', 'ai boss'] },
  { tab: 'ai_activity', label: 'AI Agents Activity',
    kw: ['ai activity', 'what did ai do', 'ai log', 'agent activity'] },
  { tab: 'cross_sell', label: 'Cross-Sell',
    kw: ['cross sell', 'cross-sell', 'upsell', 'stack coverage'] },
  { tab: 'referrals', label: 'Referrals',
    kw: ['referrals', 'referral', 'ask for referrals'] },
  { tab: 'coverage_summary', label: 'Coverage Summary',
    kw: ['coverage summary', 'coverage pdf', 'coverage report'] },
  { tab: 'daily_tracker', label: 'Daily Tracker',
    kw: ['daily tracker', 'log dials', 'log activity', 'log contacts', 'activity tracker'] },

  // ── Account / setup ─────────────────────────────────────────────
  { tab: 'settings', label: 'Settings',
    kw: ['settings', 'preferences', 'account settings', 'config', 'configuration'] },
  { tab: 'registrar', label: 'A2P Setup (Registrar)',
    kw: ['a2p', 'registrar', 'compliance registration', 'register phone', '10dlc', 'brand registration'] },
];

// Compact form to POST to the classifier. Keeps the payload small while
// giving the server everything it needs to both prompt the LLM and run a
// deterministic local match.
export function compactCatalog() {
  return NAV_CATALOG.map(({ tab, label, kw }) => ({ tab, label, kw }));
}

// Fast client-side guard: is this a real, navigable tab key we know about?
// Used before writing a #tab= hash so ARIA can never strand the user on a
// route that doesn't exist.
const KNOWN = new Set(NAV_CATALOG.map((e) => e.tab));
export function isKnownTab(tab) {
  return !!tab && KNOWN.has(String(tab));
}

export function labelForTab(tab) {
  const hit = NAV_CATALOG.find((e) => e.tab === tab);
  return hit ? hit.label : String(tab || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
